// /signup page.
//
// Creates a fresh User + Tenant + Session in one transaction (backend side).
// Same redirect-after-success pattern as /login -- the cookie is set in the
// signup response, then we full-reload so middleware picks it up.

"use client";

import { Suspense, useState } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { ApiClientError, postSignup } from "@/lib/api";

/** Validate that a redirect target is a safe relative path on this origin. */
function safeNext(raw: string | null): string {
  if (!raw) return "/projects";
  // Must be relative (starts with /) and must not be protocol-relative (//).
  // Decode first: a value like "/%2F%2Fevil.com" starts with a single "/"
  // and passes a naive string check, but browsers decode it to "//evil.com"
  // before navigating -- an open redirect. Reject on the decoded form too.
  let decoded: string;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    return "/projects";
  }
  if (
    raw.startsWith("/") &&
    !raw.startsWith("//") &&
    !decoded.startsWith("//") &&
    !decoded.startsWith("/\\")
  ) {
    return raw;
  }
  return "/projects";
}

export default function SignupPage() {
  return (
    <Suspense fallback={null}>
      <SignupForm />
    </Suspense>
  );
}

function SignupForm() {
  const search = useSearchParams();
  const next = safeNext(search.get("next"));

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [tenantName, setTenantName] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      await postSignup({
        email,
        password,
        display_name: displayName || undefined,
        tenant_name: tenantName || undefined,
      });
      window.location.href = next;
    } catch (err) {
      const msg =
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      setError(msg);
      setPending(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-zinc-50 px-4 py-12">
      <div className="w-full max-w-sm rounded-lg border border-zinc-200 bg-white p-6 shadow-sm">
        <h1 className="text-xl font-semibold text-zinc-900">
          Create a Brandform workspace
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          A new tenant and your first user, in one go.
        </p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <Field
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            autoComplete="email"
            required
          />
          <Field
            label="Password (min 8 chars)"
            type="password"
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            required
          />
          <Field
            label="Your name (optional)"
            type="text"
            value={displayName}
            onChange={setDisplayName}
            autoComplete="name"
          />
          <Field
            label="Workspace name (optional)"
            type="text"
            value={tenantName}
            onChange={setTenantName}
          />

          {error && (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={pending}
            className="inline-flex h-10 w-full items-center justify-center rounded-md bg-blue-600 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {pending ? "Creating…" : "Create workspace"}
          </button>
        </form>

        <p className="mt-4 text-center text-xs text-zinc-500">
          Already have an account?{" "}
          <Link
            href={`/login${next !== "/projects" ? `?next=${encodeURIComponent(next)}` : ""}`}
            className="font-medium text-blue-600 hover:underline"
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

function Field({
  label,
  type,
  value,
  onChange,
  autoComplete,
  required,
}: {
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-zinc-700">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        required={required}
        className="mt-1 block w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
      />
    </label>
  );
}
